import { CheckIcon, ChevronRightIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import { focusRing } from "@/lib/format";
import type { Friend } from "@/lib/api";

export type StoreConnectionTone = "neutral" | "ready" | "warning" | "danger" | "muted";

interface StoreContextStripProps {
  // Active supermarket ("Wong", "PlazaVea"), always shown so the user knows
  // which store the cart, prices and links below belong to.
  storeLabel: string;
  title: string;
  secondary?: string | null;
  tone: StoreConnectionTone;
  disabled?: boolean;
  onOpen: () => void;
  friends: Friend[];
  includedFriendIds: Set<number>;
  onToggleFriend: (id: number) => void;
  currentUserName: string;
  meAriaLabel?: string;
}

const TONE_DOT: Record<StoreConnectionTone, string> = {
  neutral: "bg-slate-300 ring-1 ring-slate-400 dark:bg-slate-600 dark:ring-slate-500",
  ready: "bg-emerald-500",
  warning: "bg-amber-500",
  danger: "bg-red-500",
  muted: "bg-slate-300 dark:bg-slate-700",
};

export function StoreContextStrip({
  storeLabel,
  title,
  secondary,
  tone,
  disabled,
  onOpen,
  friends,
  includedFriendIds,
  onToggleFriend,
  currentUserName,
}: StoreContextStripProps) {
  const hasFriends = friends.length > 0;
  return (
    <header
      role="region"
      aria-label="Contexto de compra"
      className="sticky top-0 z-30 -mx-4 border-b border-border bg-background/85 px-4 backdrop-blur-md sm:-mx-4"
    >
      <button
        type="button"
        onClick={onOpen}
        disabled={disabled}
        className={cn(
          "flex w-full min-h-12 items-center gap-3 px-1 py-2.5 text-left transition-colors",
          !disabled && "active:bg-muted/60",
          disabled && "cursor-not-allowed opacity-70",
          focusRing,
        )}
        aria-label={`${storeLabel}: ${title}`}
      >
        <span
          className={cn(
            "size-2.5 shrink-0 rounded-full ring-2 ring-background motion-safe:transition-colors",
            TONE_DOT[tone],
          )}
        />
        {/* Single row: title + optional secondary, both truncate. The secondary
            is intentionally hidden below `sm` since the only secondary today
            is the email, which is noise when the neighborhood already fits. */}
        <div className="flex min-w-0 flex-1 items-baseline gap-2">
          <span className="shrink-0 text-sm font-semibold">{storeLabel}</span>
          <span aria-hidden className="text-sm text-muted-foreground">·</span>
          <span className="truncate text-sm font-semibold">{title}</span>
          {secondary && (
            <span className="hidden truncate text-xs text-muted-foreground sm:inline">
              · {secondary}
            </span>
          )}
        </div>
        {!disabled && <ChevronRightIcon className="size-4 shrink-0 text-muted-foreground" />}
      </button>
      {hasFriends && (
        <ParticipantsRail
          currentUserName={currentUserName}
          friends={friends}
          includedFriendIds={includedFriendIds}
          onToggle={onToggleFriend}
        />
      )}
    </header>
  );
}

interface ParticipantsRailProps {
  currentUserName: string;
  friends: Friend[];
  includedFriendIds: Set<number>;
  onToggle: (friendId: number) => void;
}

export function ParticipantsRail({
  currentUserName,
  friends,
  includedFriendIds,
  onToggle,
}: ParticipantsRailProps) {
  const meInitial = initialsOf(currentUserName);
  return (
    <div
      role="group"
      aria-label="Participantes incluidos"
      // `mask-fade-right` is harmless when the rail doesn't overflow; when it
      // does (3+ friends on a narrow viewport) the soft fade hints there's
      // more content offscreen.
      className="-mx-1 flex gap-1.5 overflow-x-auto px-1 pb-2.5 pt-1 scrollbar-none mask-fade-right"
    >
      <span
        className={cn(
          "inline-flex h-9 shrink-0 items-center gap-1.5 rounded-full border border-emerald-300 bg-emerald-50 px-3 text-sm font-medium text-emerald-900 cursor-default",
          "dark:border-emerald-800 dark:bg-emerald-950 dark:text-emerald-100",
        )}
        aria-label={`Tú (${currentUserName}), siempre incluido`}
      >
        <span className="grid size-5 place-items-center rounded-full bg-emerald-200 text-[10px] font-bold text-emerald-900 dark:bg-emerald-800 dark:text-emerald-100">
          {meInitial}
        </span>
        Tú
        <CheckIcon className="size-3.5" />
      </span>
      {friends.map((friend) => {
        const isActive = includedFriendIds.has(friend.friend_id);
        return (
          <button
            key={friend.friend_id}
            type="button"
            onClick={() => onToggle(friend.friend_id)}
            data-active={isActive}
            aria-pressed={isActive}
            className={cn(
              "inline-flex h-9 shrink-0 items-center gap-1.5 rounded-full border px-3 text-sm font-medium transition-colors",
              isActive
                ? "border-emerald-300 bg-emerald-50 text-emerald-900 dark:border-emerald-800 dark:bg-emerald-950 dark:text-emerald-100"
                : "border-border bg-muted/40 text-muted-foreground hover:bg-muted",
              focusRing,
            )}
          >
            <span
              className={cn(
                "grid size-5 place-items-center rounded-full text-[10px] font-bold",
                isActive
                  ? "bg-emerald-200 text-emerald-900 dark:bg-emerald-800 dark:text-emerald-100"
                  : "bg-muted text-muted-foreground",
              )}
            >
              {initialsOf(friend.username)}
            </span>
            {friend.username}
            {isActive && <CheckIcon className="size-3.5" />}
          </button>
        );
      })}
    </div>
  );
}

function initialsOf(name: string): string {
  const trimmed = name.trim();
  if (!trimmed) return "?";
  const first = trimmed[0];
  return first ? first.toUpperCase() : "?";
}
